/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {PositionStrategy} from './position-strategy';
import {OverlayRef} from '../overlay-ref';
/**
 * A strategy for positioning overlays. Using this strategy, an overlay is given an
 * explicit position relative to the browser's viewport. We use flexbox, instead of
 * transforms, in order to avoid issues with subpixel rendering which can cause the
 * element to become blurry.
 */
export class GlobalPositionStrategy implements PositionStrategy {
/**
 * The overlay to which this strategy is attached.
 */
private _overlayRef: OverlayRef;
private _cssPosition: string = 'static';
private _topOffset: string = '';
private _bottomOffset: string = '';
private _leftOffset: string = '';
private _rightOffset: string = '';
private _alignItems: string = '';
private _justifyContent: string = '';
private _width: string = '';
private _height: string = '';
private _wrapper: HTMLElement | null = null;
/**
 * @param {?} overlayRef
 * @return {?}
 */
attach(overlayRef: OverlayRef): void {
    this._overlayRef = overlayRef;
  }
/**
 * Sets the top position of the overlay. Clears any previously set vertical position.
 * @param {?=} value New top offset.
 * @return {?}
 */
top(value = ''): this {
    this._bottomOffset = '';
    this._topOffset = value;
    this._alignItems = 'flex-start';
    return this;
  }
/**
 * Sets the left position of the overlay. Clears any previously set horizontal position.
 * @param {?=} value New left offset.
 * @return {?}
 */
left(value = ''): this {
    this._rightOffset = '';
    this._leftOffset = value;
    this._justifyContent = 'flex-start';
    return this;
  }
/**
 * Sets the bottom position of the overlay. Clears any previously set vertical position.
 * @param {?=} value New bottom offset.
 * @return {?}
 */
bottom(value = ''): this {
    this._topOffset = '';
    this._bottomOffset = value;
    this._alignItems = 'flex-end';
    return this;
  }
/**
 * Sets the right position of the overlay. Clears any previously set horizontal position.
 * @param {?=} value New right offset.
 * @return {?}
 */
right(value = ''): this {
    this._leftOffset = '';
    this._rightOffset = value;
    this._justifyContent = 'flex-end';
    return this;
  }
/**
 * Sets the overlay width and clears any previously set width.
 * @param {?=} value New width for the overlay
 * @return {?}
 */
width(value = ''): this {
    this._width = value;

    // When the width is 100%, we should reset the `left` and the offset,
    // in order to ensure that the element is flush against the viewport edge.
    if (value === '100%') {
      this.left('0px');
    }

    return this;
  }
/**
 * Sets the overlay height and clears any previously set height.
 * @param {?=} value New height for the overlay
 * @return {?}
 */
height(value = ''): this {
    this._height = value;

    // When the height is 100%, we should reset the `top` and the offset,
    // in order to ensure that the element is flush against the viewport edge.
    if (value === '100%') {
      this.top('0px');
    }

    return this;
  }
/**
 * Centers the overlay horizontally with an optional offset.
 * Clears any previously set horizontal position.
 * 
 * @param {?=} offset Overlay offset from the horizontal center.
 * @return {?}
 */
centerHorizontally(offset = ''): this {
    this.left(offset);
    this._justifyContent = 'center';
    return this;
  }
/**
 * Centers the overlay vertically with an optional offset.
 * Clears any previously set vertical position.
 * 
 * @param {?=} offset Overlay offset from the vertical center.
 * @return {?}
 */
centerVertically(offset = ''): this {
    this.top(offset);
    this._alignItems = 'center';
    return this;
  }
/**
 * Apply the position to the element.
 * \@docs-private 
 * 
 * @return {?} Resolved when the styles have been applied.
 */
apply(): void {
    const /** @type {?} */ element = this._overlayRef.overlayElement;

    if (!this._wrapper && element.parentNode) {
      this._wrapper = document.createElement('div');
      this._wrapper.classList.add('cdk-global-overlay-wrapper');
      element.parentNode.insertBefore(this._wrapper, element);
      this._wrapper.appendChild(element);
    }

    let /** @type {?} */ styles = element.style;
    let /** @type {?} */ parentStyles = ( /** @type {?} */((element.parentNode as HTMLElement))).style;

    styles.position = this._cssPosition;
    styles.marginTop = this._topOffset;
    styles.marginLeft = this._leftOffset;
    styles.marginBottom = this._bottomOffset;
    styles.marginRight = this._rightOffset;
    styles.width = this._width;
    styles.height = this._height;

    parentStyles.justifyContent = this._justifyContent;
    parentStyles.alignItems = this._alignItems;
  }
/**
 * Removes the wrapper element from the DOM.
 * @return {?}
 */
dispose(): void {
    if (this._wrapper && this._wrapper.parentNode) {
      this._wrapper.parentNode.removeChild(this._wrapper);
      this._wrapper = null;
    }
  }
}

function GlobalPositionStrategy_tsickle_Closure_declarations() {
/**
 * The overlay to which this strategy is attached.
 * @type {?}
 */
GlobalPositionStrategy.prototype._overlayRef;
/** @type {?} */
GlobalPositionStrategy.prototype._cssPosition;
/** @type {?} */
GlobalPositionStrategy.prototype._topOffset;
/** @type {?} */
GlobalPositionStrategy.prototype._bottomOffset;
/** @type {?} */
GlobalPositionStrategy.prototype._leftOffset;
/** @type {?} */
GlobalPositionStrategy.prototype._rightOffset;
/** @type {?} */
GlobalPositionStrategy.prototype._alignItems;
/** @type {?} */
GlobalPositionStrategy.prototype._justifyContent;
/** @type {?} */
GlobalPositionStrategy.prototype._width;
/** @type {?} */
GlobalPositionStrategy.prototype._height;
/** @type {?} */
GlobalPositionStrategy.prototype._wrapper;
}




// WEBPACK FOOTER //
// ../../../src/cdk/overlay/position/global-position-strategy.ts